import React from 'react';
import { RegistrationShell } from './registration';
import { InvitationCode } from './pages/invitation-code/invitation-code';
import { EditEmail } from './pages/verify-email/verify-email';
import { ConfirmYourProfile } from './pages/welcome/confirm-your-profile';
import { PersonalizeStep } from './pages/personalize-your-experience/personalize-your-experience';
import { NumberVerificationStep } from './pages/number-verification/number-verification';
import { UnlockFasterStep } from './pages/unlock-faster/unlock-faster';
import { GoodToHaveYou } from './pages/good-to-have-you/good-to-have-you';
import { PreparingYourProfile } from './pages/preparing-your-profile/preparing-your-profile';
import { VerifyEmail } from './pages/email-funnel/email-funnel';
import { SecureAccountStep } from './pages/secure-your-account/secure-your-account';
import { QrCodeScreen } from './pages/qr-code/qr-code';
import { AllSetStep } from './all-set/all-set';
import { useAuth } from '../../contexts/auth/auth-context';
import { AppLoader } from '../../components/app-loader/app-loader';
import { readStoredTokens } from '../../contexts/auth/helpers';
import { useVisitorHeartbeat } from '../../hooks/use-visitor-heartbeat';
import { usePromptQueue } from '../../contexts/prompt-queue-context';

export const RegistrationShellWrapper: React.FC = () => {
  useVisitorHeartbeat('signup');

  // Fire prompt queue on first app open after install (attempt 1)
  const { fireEvent } = usePromptQueue();
  const firedRef = React.useRef(false);
  React.useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    fireEvent('app_first_open');
  }, [fireEvent]);

  const { profileLoading, sessionLoading, profile } = useAuth();
  
  // If user has auth tokens, wait for profile to load before showing registration shell
  // This prevents flashing step 0 before jumping to the saved step
  const tokens = readStoredTokens();
  const hasTokens = !!tokens.access_token && !!tokens.refresh_token;
  
  // Wait for boot to complete if user has tokens
  if (hasTokens && (sessionLoading || profileLoading || !profile)) {
    return <AppLoader active={true} label="Loading your progress..." />;
  }

  return (
    <RegistrationShell
      steps={[
        InvitationCode,       // 0  - Web: Enter invitation code
        EditEmail,            // 1  - Web: Enter email, send OTP
        VerifyEmail,          // 2  - Web: Verify email OTP
        SecureAccountStep,    // 3  - Web: Set password (creates account)
        QrCodeScreen,         // 4  - Web: Scan QR / download app (terminal on web)
        NumberVerificationStep, // 5  - App: Phone verification
        UnlockFasterStep,     // 6  - App: Biometrics setup
        GoodToHaveYou,        // 7  - App: Welcome message
        ConfirmYourProfile,   // 8  - App: Profile details
        PreparingYourProfile, // 9  - App: Loading transition
        PersonalizeStep,      // 10 - App: Timezone & notifications
        AllSetStep,           // 11 - App: Finish registration
      ]}
    />
  );
};
